import {
  Box,
  Checkbox,
  styled,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { invoiceTableHeaders } from '../../../invoice.constants'
import {
  listTableBodyCellStyles,
  listTableHeaderCellStyles,
} from 'components/list-table/list-table.styles'

const StyledTableCell = styled(TableCell)({
  ...listTableHeaderCellStyles,
  ...listTableBodyCellStyles,
})

const COLUMN_WIDTHS: Record<string, string> = {
  invoice: '16%',
  company: '20%',
  aging: '10%',
  amount: '12%',
  documents: '16%',
  action: '26%',
}

const COLUMN_WIDTHS_WITH_BATCH: Record<string, string> = {
  invoice: '14%',
  company: '16%',
  aging: '9%',
  amount: '10%',
  documents: '14%',
  batch: '10%',
  action: '27%',
}

type SortColumn = 'invoice' | 'company' | 'aging' | 'amount' | null
type SortDirection = 'asc' | 'desc'

const Triangle = styled('span')<{
  direction: 'up' | 'down'
  active?: boolean
}>(({ direction, active, theme }) => ({
  marginLeft: 6,
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  ...(direction === 'down'
    ? { borderTop: `10px solid ${active ? theme.palette.text.primary : theme.palette.text.disabled}` }
    : { borderBottom: `10px solid ${active ? theme.palette.text.primary : theme.palette.text.disabled}` }),
}))

interface InvoicesTableHeadersProps {
  allSelected: boolean
  someSelected: boolean
  onSelectAll: (selected: boolean) => void
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSortChange: (column: Exclude<SortColumn, null>) => void
  showBatchColumn?: boolean
}

const InvoicesTableHeaders = ({
  allSelected,
  someSelected,
  onSelectAll,
  sortColumn,
  sortDirection,
  onSortChange,
  showBatchColumn = false,
}: InvoicesTableHeadersProps) => {
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.target.checked)
  }

  const widths = showBatchColumn ? COLUMN_WIDTHS_WITH_BATCH : COLUMN_WIDTHS

  return (
    <TableHead>
      <TableRow>
        {invoiceTableHeaders.map((header, index) => {
          switch (index) {
            case 0:
              return (
                <StyledTableCell
                  key={header.id}
                  sx={{ width: widths.invoice }}
                  align="left"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    columnGap={1}
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={handleSelectAll}
                      sx={{ padding: '4px' }}
                    />
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onSortChange('invoice')}
                    >
                      <Box component="span">{header.label}</Box>
                      <Triangle direction={sortDirection === 'asc' ? 'up' : 'down'} active={sortColumn === 'invoice'} />
                    </Box>
                  </Box>
                </StyledTableCell>
              )
            case 1:
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.company }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSortChange('company')}
                  >
                    <Box component="span">{header.label}</Box>
                    <Triangle direction={sortDirection === 'asc' ? 'up' : 'down'} active={sortColumn === 'company'} />
                  </Box>
                </StyledTableCell>
              )
            case 2:
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.aging }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSortChange('aging')}
                  >
                    <Box component="span">{header.label}</Box>
                    <Triangle direction={sortDirection === 'asc' ? 'up' : 'down'} active={sortColumn === 'aging'} />
                  </Box>
                </StyledTableCell>
              )
            case 3:
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.amount }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSortChange('amount')}
                  >
                    <Box component="span">{header.label}</Box>
                    <Triangle direction={sortDirection === 'asc' ? 'up' : 'down'} active={sortColumn === 'amount'} />
                  </Box>
                </StyledTableCell>
              )
            case 4:
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.documents }}>
                  {header.label}
                </StyledTableCell>
              )
            case 5:
              if (!showBatchColumn) {
                return null
              }
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.batch }}>
                  {header.label}
                </StyledTableCell>
              )
            case 6:
              return (
                <StyledTableCell key={header.id} sx={{ width: widths.action }}>
                  {header.label}
                </StyledTableCell>
              )
          }
        })}
      </TableRow>
    </TableHead>
  )
}

export default InvoicesTableHeaders
